import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, MapPin, Pencil } from 'lucide-react';
import {
  trackerFdfcDispatchProblems,
  type TrackerFdfcAddressInput,
  type TrackerFdfcDetail,
} from '@tracker/shared';
import { trackerFdfcService } from '@/services/tracker-fdfc.service';
import { formatDateTime } from '@/lib/dateFormat';
import { getErrorMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DetailSection } from './DetailSection';

type Draft = Record<keyof TrackerFdfcAddressInput, string>;

const FIELDS: { key: keyof Draft; label: string; maxLength: number; wide?: boolean; placeholder?: string }[] = [
  { key: 'full_name', label: 'Recipient name', maxLength: 150, wide: true },
  { key: 'address_line1', label: 'Address line 1', maxLength: 255, wide: true },
  { key: 'address_line2', label: 'Address line 2', maxLength: 255, wide: true },
  { key: 'city', label: 'City', maxLength: 100 },
  { key: 'state', label: 'State', maxLength: 100 },
  { key: 'pincode', label: 'Pincode', maxLength: 20, placeholder: '6-digit PIN' },
  { key: 'country', label: 'Country', maxLength: 100, placeholder: 'India' },
  { key: 'alt_phone', label: 'Alternate phone', maxLength: 20 },
];

function draftFrom(e: TrackerFdfcDetail): Draft {
  return {
    full_name: e.full_name ?? '',
    address_line1: e.address_line1 ?? '',
    address_line2: e.address_line2 ?? '',
    city: e.city ?? '',
    state: e.state ?? '',
    pincode: e.pincode ?? '',
    country: e.country ?? '',
    alt_phone: e.alt_phone ?? '',
  };
}

/** Already with NSDC (or may be): an edit here does not change that order. */
const SENT = ['dispatching', 'dispatched', 'unknown'];

/**
 * The courier address, editable by the team. A hand edit stands until the next
 * webhook or Excel row, which replaces it — the newest data always wins.
 */
export function AddressSection({ entry, canUpdate }: { entry: TrackerFdfcDetail; canUpdate: boolean }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(entry));

  const save = useMutation({
    mutationFn: () => trackerFdfcService.updateAddress(entry.id, draft),
    onSuccess: () => {
      toast.success('Address saved');
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ['tracker-fdfc'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const unchanged = (Object.keys(draft) as (keyof Draft)[]).every(
    (k) => draft[k].trim() === (draftFrom(entry)[k] ?? '').trim(),
  );

  const startEdit = () => {
    setDraft(draftFrom(entry));
    setEditing(true);
  };

  // NSDC's checks on what is being typed. Mobile and dispatch state are not
  // edited here, so they come from the entry / a neutral stand-in.
  const nsdcProblems = editing
    ? trackerFdfcDispatchProblems({
        ...draft,
        mobile_last10: entry.mobile_last10,
        dispatch_status: 'not_dispatched',
      })
    : [];

  const lines = [
    entry.address_line1,
    entry.address_line2,
    [entry.city, entry.state].filter(Boolean).join(', '),
    [entry.pincode, entry.country].filter(Boolean).join(' · '),
  ].filter(Boolean);

  return (
    <DetailSection
      icon={MapPin}
      title="Courier address"
      action={
        canUpdate && !editing ? (
          <Button size="sm" variant="outline" onClick={startEdit}>
            <Pencil className="h-3.5 w-3.5" /> Edit
          </Button>
        ) : null
      }
    >

      {editing ? (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            // Nothing changed: nothing to save.
            if (unchanged) setEditing(false);
            else save.mutate();
          }}
        >
          {SENT.includes(entry.dispatch_status) && (
            <p className="flex items-start gap-1.5 rounded-md bg-amber-100 px-2.5 py-1.5 text-xs text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Already sent to NSDC — this change won&apos;t update the order there.
            </p>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {FIELDS.map((f) => (
              <label key={f.key} className={f.wide ? 'block sm:col-span-2' : 'block'}>
                <span className="text-xs font-medium text-muted-foreground">{f.label}</span>
                <Input
                  className="mt-1 sm:h-9"
                  value={draft[f.key]}
                  maxLength={f.maxLength}
                  placeholder={f.placeholder}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                />
              </label>
            ))}
          </div>

          {nsdcProblems.length > 0 && (
            <div className="rounded-md bg-amber-100 px-2.5 py-1.5 text-xs text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">
              <p className="font-medium">NSDC will refuse this:</p>
              <ul className="mt-0.5 list-disc pl-4">
                {nsdcProblems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setEditing(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save address'}
            </Button>
          </div>
        </form>
      ) : lines.length ? (
        // The alternate phone is shown under Contact; it is still edited here.
        <address className="rounded-lg bg-muted/40 p-3 text-sm not-italic leading-relaxed">
          {entry.full_name && <div className="font-medium">{entry.full_name}</div>}
          {lines.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </address>
      ) : (
        <p className="text-sm text-muted-foreground">No address received.</p>
      )}

      {!editing && entry.address_edited_at && (
        <p className="mt-2 text-xs text-muted-foreground">
          Edited{entry.address_edited_by_name ? ` by ${entry.address_edited_by_name}` : ''} ·{' '}
          {formatDateTime(entry.address_edited_at)} · the next webhook update will replace it
        </p>
      )}
    </DetailSection>
  );
}
